import { useEffect } from "react";

const SITE_NAME = "Joneten Saputra";
const DEFAULT_TITLE_SUFFIX = " — Product Manager & Technical Solution Specialist";

/** ensure a <meta name|property="..."> tag exists & set its content */
const setMeta = (key, value, useProperty = false) => {
  if (typeof document === "undefined") return;
  const attr = useProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  if (value == null || value === "") {
    el.removeAttribute("content");
  } else {
    el.setAttribute("content", value);
  }
};

const setLink = (rel, href) => {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  if (href) el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  if (typeof document === "undefined") return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const removeNode = (selector) => {
  if (typeof document === "undefined") return;
  const el = document.querySelector(selector);
  if (el) el.remove();
};

/**
 * Apply <head> SEO metadata. Re-runs whenever inputs change.
 * Notes:
 * - Client-side meta injection works for Google (executes JS) and for browser tab UX.
 * - For Twitter/Facebook crawlers (no JS), you'll need SSR or a meta-prerender worker.
 */
export const useSeo = ({
  title,
  description,
  image,
  url,
  type = "website",
  category,
  publishedAt,
  keywords = [],
  author = SITE_NAME,
} = {}) => {
  useEffect(() => {
    const finalTitle = title
      ? (title.toLowerCase().includes(SITE_NAME.toLowerCase()) ? title : `${title} | ${SITE_NAME}`)
      : `${SITE_NAME}${DEFAULT_TITLE_SUFFIX}`;
    const finalDescription =
      description ||
      "Product Manager & Technical Solution Specialist — AI, IoT, software, hardware, smart energy, system integration.";
    const finalUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const finalImage =
      image ||
      (typeof window !== "undefined"
        ? `${window.location.origin}/api/og/site.png`
        : "");

    // <title>
    if (typeof document !== "undefined") {
      document.title = finalTitle;
    }

    // standard
    setMeta("description", finalDescription);
    setMeta("author", author);
    if (keywords?.length) setMeta("keywords", keywords.join(", "));

    // canonical
    setLink("canonical", finalUrl);

    // Open Graph
    setMeta("og:title", finalTitle, true);
    setMeta("og:description", finalDescription, true);
    setMeta("og:image", finalImage, true);
    setMeta("og:url", finalUrl, true);
    setMeta("og:type", type, true);
    setMeta("og:site_name", SITE_NAME, true);
    setMeta("og:locale", "id_ID", true);

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", finalTitle);
    setMeta("twitter:description", finalDescription);
    setMeta("twitter:image", finalImage);

    // Article-specific
    if (type === "article") {
      if (publishedAt) setMeta("article:published_time", publishedAt, true);
      if (category) setMeta("article:section", category, true);
      if (author) setMeta("article:author", author, true);

      setJsonLd("jsonld-article", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description: finalDescription,
        image: finalImage,
        author: { "@type": "Person", name: author, url: typeof window !== "undefined" ? window.location.origin : "" },
        publisher: { "@type": "Person", name: SITE_NAME },
        datePublished: publishedAt,
        mainEntityOfPage: finalUrl,
      });
    } else {
      removeNode("#jsonld-article");
      setJsonLd("jsonld-person", {
        "@context": "https://schema.org",
        "@type": "Person",
        name: SITE_NAME,
        jobTitle: "Product Manager & Technical Solution Specialist",
        url: typeof window !== "undefined" ? window.location.origin : "",
        sameAs: ["https://www.linkedin.com/in/joneten-saputra-913816105/"],
        email: "mailto:joneten91@gmail.com",
      });
    }
  }, [title, description, image, url, type, category, publishedAt, JSON.stringify(keywords), author]);
};
